/**
 * User-facing copy while SMSWiser drops are loading.
 * For Media Devoted | Created By Ciro Autiero
 */

export interface DropsLoadingMessageParams {
  fetchLength: number;
  /** YYYY-MM-DD — range sent to the API (not draft filters). */
  fetchedFrom: string;
  fetchedTo: string;
  slowLoading: boolean;
  variant?: 'drops' | 'deployed';
}

function ymdToDmy(ymd: string): string {
  const [yyyy, mm, dd] = ymd.split('-');
  if (!yyyy || !mm || !dd) return ymd;
  return `${dd}/${mm}/${yyyy}`;
}

export function getDropsLoadingMessage(params: DropsLoadingMessageParams): {
  title: string;
  detail: string | null;
} {
  const count = Math.max(1, params.fetchLength).toLocaleString();
  const hasRange = Boolean(params.fetchedFrom && params.fetchedTo);

  if (hasRange) {
    const from = ymdToDmy(params.fetchedFrom);
    const to = ymdToDmy(params.fetchedTo);
    const estimate = Math.max(1, params.fetchLength).toLocaleString();
    return {
      title: `Fetching drops for ${from} → ${to}…`,
      detail:
        `Paginating SMSWiser in 5k chunks until your start date is covered (est. ~${estimate} rows). ` +
        'This can take several minutes. Filters apply to the loaded batch.',
    };
  }

  const noun = params.variant === 'deployed' ? 'deployed drops' : 'drops';
  return {
    title: `Fetching up to ${count} ${noun} from SMSWiser…`,
    detail: params.slowLoading
      ? 'Logging into SMSWiser or refreshing the session — this can take a moment on first load.'
      : null,
  };
}

/** True when the list should show the full-page loader (incl. date-range refetch). */
export function shouldShowDropsListLoading(params: {
  isLoading: boolean;
  isFetching: boolean;
  fetchedFrom: string;
  fetchedTo: string;
}): boolean {
  if (params.isLoading) return true;
  return Boolean(params.isFetching && params.fetchedFrom && params.fetchedTo);
}
